import {
  FloatingLabel,
  InputGroup,
  Form,
  FormControlProps,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { forwardRef, useEffect, useRef } from "react";
import {
  FormatarCPF,
  FormatarDouble,
  FormatarTelefone,
  SoNumeros,
  RemoverZerosEsquerda,
  FormatarCNPJ,
  TodasMaiusculas,
  FormatarCEP,
  Capitalizar,
  TodasMinusculas,
  FormatarDataHora,
  FormatarDecimal,
} from "src/hook/Functions";
import { FieldError } from "react-hook-form";

type InputProps = {
  label: string;
  type?: FormControlProps["type"];
  size?: FormControlProps["size"];
  errorsMessage?: FieldError | undefined;
  inputMask?:
    | "int"
    | "double"
    | "decimal"
    | "decimal2"
    | "decimal3"
    | "text"
    | "textarea"
    | "cpf"
    | "cnpj"
    | "cnpj-cpf"
    | "fone"
    | "data"
    | "datahora"
    | "minusculas"
    | "maiusculas"
    | "cep"
    | "capitalizar";
  maxLength?: number;
  disabled?: boolean | undefined;
  readOnly?: boolean | undefined;
  onInputChange?: (value: string) => void;
  registerField: any;
  tooltipMessage?: string;
  textAlign?: "center";
  autoComplete?: boolean;
  useFloatingLabel?: boolean;
  autoFocus?: boolean;
};

export const FloatInputComponent = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      type = "text",
      size = "sm",
      errorsMessage,
      inputMask = "text",
      maxLength,
      disabled = undefined,
      readOnly = undefined,
      onInputChange,
      registerField,
      tooltipMessage = "",
      textAlign = undefined,
      autoComplete = false,
      useFloatingLabel = true,
      autoFocus = undefined,
    },
    ref,
  ) => {
    const {
      onChange: onChangeField,
      onBlur: onBlurField,
      name: nameField,
      ref: refField,
    } = registerField;

    if (inputMask === "data") {
      type = "date";
    }
    if (inputMask === "datahora") {
      type = "text";
    }

    const autoResize = (el: HTMLTextAreaElement) => {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    };

    const OnInputChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      const value = e.target.value;
      onInputChange && onInputChange(value);
      const sanitizedValue = RemoverZerosEsquerda(SoNumeros(value));
      const cursorPosition = e.target.selectionStart;
      const cursorPoritionEnd =
        e.target.selectionStart === e.target.value.length;

      const nativeEvent = e.nativeEvent as InputEvent;
      if (nativeEvent.inputType === "deleteContentBackward") {
        // A tecla Backspace foi pressionada
        // return;
      }

      if (inputMask === "double" || inputMask === "int") {
        if (sanitizedValue.length === 0) {
          e.target.value = "0";
          return;
        }
      }

      if (inputMask === "double") {
        e.target.value = FormatarDouble(sanitizedValue);
      }

      if (inputMask === "int") {
        e.target.value = sanitizedValue;
      }

      if (inputMask === "decimal" || inputMask === "decimal2") {
        e.target.value = FormatarDecimal(value, 2);
        if (!cursorPoritionEnd && cursorPosition !== null) {
          e.target.setSelectionRange(cursorPosition, cursorPosition);
        }
      }

      if (inputMask === "decimal3") {
        e.target.value = FormatarDecimal(value, 3);
        if (!cursorPoritionEnd && cursorPosition !== null) {
          e.target.setSelectionRange(cursorPosition, cursorPosition);
        }
      }

      if (inputMask === "cpf") {
        e.target.value = FormatarCPF(value);
        if (!cursorPoritionEnd) {
          e.target.setSelectionRange(cursorPosition, cursorPosition);
        }
      }

      if (inputMask === "cnpj") {
        e.target.value = FormatarCNPJ(value);
        if (!cursorPoritionEnd) {
          e.target.setSelectionRange(cursorPosition, cursorPosition);
        }
      }

      if (inputMask === "cnpj-cpf") {
        if (SoNumeros(value).length === 14) {
          e.target.value = FormatarCNPJ(value);
        }
        if (SoNumeros(value).length === 11) {
          e.target.value = FormatarCPF(value);
        }
        if (!cursorPoritionEnd) {
          e.target.setSelectionRange(cursorPosition, cursorPosition);
        }
      }

      if (inputMask === "fone") {
        e.target.value = FormatarTelefone(value);
      }

      if (inputMask === "maiusculas") {
        e.target.value = TodasMaiusculas(value);
        e.target.setSelectionRange(cursorPosition, cursorPosition);
      }

      if (inputMask === "minusculas") {
        e.target.value = TodasMinusculas(value);
        e.target.setSelectionRange(cursorPosition, cursorPosition);
      }

      if (inputMask === "cep") {
        e.target.value = FormatarCEP(value);
        if (!cursorPoritionEnd) {
          e.target.setSelectionRange(cursorPosition, cursorPosition);
        }
      }

      if (inputMask === "capitalizar") {
        e.target.value = Capitalizar(value);
        e.target.setSelectionRange(cursorPosition, cursorPosition);
      }
      if (inputMask === "textarea") {
        const target = e.target as HTMLTextAreaElement;
        autoResize(target);
      }
      if (inputMask === "datahora") {
        e.target.value = FormatarDataHora(value);
      }

      onChangeField(e);
    };

    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    useEffect(() => {
      if (inputMask === "textarea" && textareaRef.current) {
        autoResize(textareaRef.current);
      }
    }, []);

    // const localRef = useRef<HTMLInputElement | null>(null);

    // useEffect(() => {
    //   if (inputMask === "datahora" && localRef.current) {
    //     const raw = localRef.current.value;
    //     if (!raw) return;

    //     // Tenta ISO primeiro
    //     const mIso = moment(raw, moment.ISO_8601, true); // parse estrito ISO [web:5][web:24]
    //     if (mIso.isValid()) {
    //       localRef.current.value = mIso.format("DD/MM/YYYY HH:mm:ss");
    //       return;
    //     }
    //     // Tenta já em BR
    //     const mBr = moment(raw, "DD/MM/YYYY HH:mm:ss", true);
    //     if (mBr.isValid()) {
    //       localRef.current.value = mBr.format("DD/MM/YYYY HH:mm:ss");
    //     }
    //   }
    // }, []);

    const inputElement =
      inputMask === "textarea" ? (
        <Form.Control
          as="textarea"
          rows={1}
          placeholder={label}
          size={size}
          isInvalid={!!errorsMessage?.message}
          ref={(e: HTMLTextAreaElement | null) => {
            refField(e);
            textareaRef.current = e;
          }}
          onChange={(e) => {
            OnInputChange(e);
          }}
          onBlur={onBlurField}
          name={`${nameField}`}
          maxLength={maxLength}
          disabled={disabled}
          readOnly={readOnly}
          className={textAlign === "center" ? "text-center" : ""}
          autoComplete={autoComplete === true ? "on" : ""}
          style={{
            resize: "none",
            overflow: "hidden",
          }}
        />
      ) : (
        <Form.Control
          type={type}
          placeholder={label}
          size={size}
          isInvalid={!!errorsMessage?.message}
          ref={refField}
          onChange={(e) => {
            OnInputChange(e);
          }}
          inputMode={
            inputMask == "double" ||
            inputMask == "int" ||
            inputMask == "decimal" ||
            inputMask == "decimal2" ||
            inputMask == "decimal3" ||
            inputMask == "datahora"
              ? "numeric"
              : undefined
          }
          onBlur={onBlurField}
          name={`${nameField}`}
          maxLength={maxLength}
          disabled={disabled}
          readOnly={readOnly}
          className={textAlign === "center" ? "text-center" : ""}
          autoComplete={autoComplete === true ? "on" : ""}
          autoFocus={autoFocus}
        />
      );

    const wrappedInput = tooltipMessage ? (
      <OverlayTrigger
        placement="bottom-start"
        overlay={
          <Tooltip id={`tooltip-${nameField}`}>{tooltipMessage}</Tooltip>
        }
        trigger={["hover", "focus"]}
      >
        {inputElement}
      </OverlayTrigger>
    ) : (
      inputElement
    );

    return (
      <InputGroup hasValidation>
        {useFloatingLabel ? (
          <FloatingLabel label={label}>
            {wrappedInput}
            {errorsMessage?.message?.toString() && (
              <Form.Control.Feedback type="invalid">
                {errorsMessage.message.toString()}
              </Form.Control.Feedback>
            )}
          </FloatingLabel>
        ) : (
          <>
            {wrappedInput}
            {errorsMessage?.message?.toString() && (
              <Form.Control.Feedback type="invalid">
                {errorsMessage.message.toString()}
              </Form.Control.Feedback>
            )}
          </>
        )}
      </InputGroup>
    );
  },
);
